import React, { useRef, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Navbar.css";
import { menuData } from "./menuData";
import { useAuth } from "../utils/authContext";

function Navbar() {
  const [infoMenuOpen, setInfoMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeMegaMenu, setActiveMegaMenu] = useState(null);
  const [activeBlockSubmenu, setActiveBlockSubmenu] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [clickedDropdown, setClickedDropdown] = useState(null); // Track clicked dropdowns
  const menuItemRefs = useRef({});
  const navbarRef = useRef(null);
  const infoMenuRef = useRef(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        if (clickedDropdown !== null) {
          setActiveDropdown(null);
          setActiveMegaMenu(null);
          setClickedDropdown(null);
        }
      }
      if (infoMenuRef.current && !infoMenuRef.current.contains(event.target)) {
        setInfoMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [clickedDropdown]);

  // Handle dropdown on desktop (hover)
  const handleMouseEnter = (itemId) => {
    // Only open on hover if it wasn't opened by click
    if (clickedDropdown !== itemId) {
      setActiveDropdown(itemId);
      if (menuData.find(item => item.id === itemId)?.megaMenu) {
        setActiveMegaMenu(itemId);
      }
      
      // Calculate position for fixed dropdown
      const element = menuItemRefs.current[itemId];
      if (element) {
        const rect = element.getBoundingClientRect();
        setDropdownPos({
          top: rect.bottom + 8,
          left: rect.left
        });
      }
    }
  };

  const handleMouseLeave = () => {
    // Don't close if any dropdown was opened by click
    if (clickedDropdown === null) {
      setActiveDropdown(null);
      setActiveMegaMenu(null);
    }
  };

  // Handle dropdown on mobile (click) - stays open until clicked again
  const handleDropdownClick = (itemId, event) => {
    event.stopPropagation();
    
    // If this dropdown was already clicked, close it
    if (clickedDropdown === itemId) {
      setActiveDropdown(null);
      setActiveMegaMenu(null);
      setClickedDropdown(null);
      return;
    }
    
    // Otherwise, open this one and set as clicked
    setActiveDropdown(itemId);
    setClickedDropdown(itemId);
    
    const menuItem = menuData.find(item => item.id === itemId);
    if (menuItem?.megaMenu) {
      setActiveMegaMenu(itemId);
    }
    
    // Calculate position for fixed dropdown
    const element = menuItemRefs.current[itemId];
    if (element) {
      const rect = element.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        left: rect.left
      });
    }
  };

  const handleBlockSubmenuClick = (blockName) => {
    setActiveBlockSubmenu(activeBlockSubmenu === blockName ? null : blockName);
  };

  const closeMobileMenu = () => {
    setActiveDropdown(null);
    setActiveMegaMenu(null);
    setActiveBlockSubmenu(null);
    setClickedDropdown(null);
  };

  return (
    <>
      {/* Main Navbar */}
      <nav 
        ref={navbarRef}
        className="navbar"
      >
        <div className="navbar-container">
          {/* Info Menu Button - Leftmost */}
          <div className="info-menu-container" ref={infoMenuRef}>
            <button
              className="info-menu-btn"
              onClick={() => setInfoMenuOpen(!infoMenuOpen)}
              aria-label="Toggle info menu"
              title="मिशन, टीम, फीडबैक"
            >
              ☰
            </button>

            {infoMenuOpen && (
              <div className="info-menu-dropdown">
                <div className="info-menu-header">
                  <h3>मेनू</h3>
                  <button
                    className="info-menu-close"
                    onClick={() => setInfoMenuOpen(false)}
                    aria-label="Close info menu"
                  >
                    ✕
                  </button>
                </div>
                <div className="info-menu-content">
                  <Link
                    to="/mission"
                    className="info-menu-link"
                    onClick={() => setInfoMenuOpen(false)}
                  >
                    मिशन
                  </Link>
                  <Link
                    to="/team"
                    className="info-menu-link"
                    onClick={() => setInfoMenuOpen(false)}
                  >
                    टीम
                  </Link>
                  <Link
                    to="/feedback"
                    className="info-menu-link"
                    onClick={() => setInfoMenuOpen(false)}
                  >
                    फीडबैक
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Left Sidebar Toggle */}
          {/* <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle sidebar menu"
          >
            ☰
          </button> */}

          {/* Logo */}
          <div className="navbar-logo">
            <Link to="/" className="logo-link">
              लाठ साब 
            </Link>
          </div>

          {/* Main Navigation Menu */}
          <div className={`navbar-menu ${activeDropdown ? "active" : ""}`}>
            {menuData.map((item) => (
              <div
                key={item.id}
                className="menu-item"
                ref={(el) => (menuItemRefs.current[item.id] = el)}
                onMouseEnter={() => handleMouseEnter(item.id)}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  className={`menu-link ${activeDropdown === item.id ? "active" : ""}`}
                  onClick={(e) => handleDropdownClick(item.id, e)}
                >
                  {item.title}
                  {item.submenu.length > 0 && (
                    <span className="dropdown-arrow">▼</span>
                  )}
                </button>

                {/* Standard Dropdown Menu */}
                {item.submenu.length > 0 && !item.megaMenu && (
                  <div
                    className={`dropdown-menu ${
                      activeDropdown === item.id ? "active" : ""
                    }`}
                    style={{
                      top: `${dropdownPos.top || 0}px`,
                      left: `${dropdownPos.left || 0}px`,
                      marginTop: "12px"
                    }}
                  >
                    {item.submenu.map((subitem, index) => (
                      <Link
                        key={index}
                        to={subitem.link}
                        className="dropdown-link"
                        onClick={closeMobileMenu}
                      >
                        {subitem.label}
                      </Link>
                    ))}
                  </div>
                )}

                {/* Mega Menu for Blocks */}
                {item.megaMenu && (
                  <div
                    className={`mega-menu ${
                      activeMegaMenu === item.id ? "active" : ""
                    }`}
                    style={{
                      top: `${dropdownPos.top}px`,
                      left: `${dropdownPos.left}px`,
                      marginTop: "12px"
                    }}
                  >
                    <div className="mega-menu-content">
                      <h3 className="mega-menu-title">{item.megaMenu.title}</h3>
                      <div className="blocks-grid">
                        {item.megaMenu.blocks.map((block, blockIndex) => (
                          <div key={blockIndex} className="block-section">
                            <div
                              className="block-header"
                              onClick={() => handleBlockSubmenuClick(block.name)}
                            >
                              <h4>{block.name}</h4>
                              <span className="block-toggle">▼</span>
                            </div>
                            <div
                              className={`village-list ${
                                activeBlockSubmenu === block.name ? "active" : ""
                              }`}
                            >
                              {block.villages.map((village, villageIndex) => (
                                <Link
                                  key={villageIndex}
                                  to={village.link || `/village/${village.name}`}
                                  className="village-link"
                                  onClick={closeMobileMenu}
                                >
                                  {village.name || village}
                                </Link>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Submenu items at bottom of mega menu */}
                      <div className="mega-menu-footer">
                        {item.submenu.map((subitem, index) => (
                          <Link
                            key={index}
                            to={subitem.link}
                            className="mega-menu-link"
                            onClick={closeMobileMenu}
                          >
                            {subitem.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right Section - Auth Buttons */}
          <div className="navbar-right">
            {!user ? (
              <>
                <Link to="/signup" className="auth-btn auth-btn-signup">
                  साइन अप
                </Link>
              </>
            ) : (
              <>
                {/* Admin Dashboard Button */}
                {user.role === 'admin' && (
                  <Link 
                    to="/admin/dashboard" 
                    className="auth-btn auth-btn-admin"
                    style={{ 
                      marginRight: '10px',
                      backgroundColor: 'var(--primary)',
                      color: 'white'
                    }}
                  >
                    📊 Dashboard
                  </Link>
                )}
                
                {/* Logout Button */}
                <button
                  onClick={() => {
                    logout();
                    navigate("/");
                  }}
                  className="auth-btn auth-btn-login"
                  style={{ cursor: "pointer" }}
                >
                  लॉगआउट
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Left Sidebar Menu */}
      {/* <div className={`sidebar ${sidebarOpen ? "active" : ""}`}>
        <div className="sidebar-header">
          <h3>मेनू</h3>
          <button
            className="sidebar-close"
            onClick={closeSidebar}
            aria-label="Close sidebar"
          >
            ✕
          </button>
        </div>

        <div className="sidebar-content">
          {sidebarMenuData.map((item, index) => (
            <Link
              key={index}
              to={item.link}
              className="sidebar-link"
              onClick={closeSidebar}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div> */}

      {/* Sidebar Overlay */}
      {/* {sidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar}></div>
      )} */}
    </>
  );
}

export default Navbar;
